/** Weekly schedule of a recurring shop banner (Nightly sale, Weekend sale).
 *  Times are local wall-clock: New York time for US, CA and MX shoppers,
 *  Brussels time for everyone else (the theme applies it). */

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const LOCAL_TIME_NOTE =
  'Times are local: New York time for US, CA and MX shoppers, Brussels time for everyone else.';

export const END_OFFSETS = [
  { value: 0, label: 'same day' },
  { value: 1, label: 'next day' },
  ...[2, 3, 4, 5, 6, 7].map((n) => ({ value: n, label: `${n} days later` })),
];

export const toHHMM = (minute: number): string => {
  const m = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** 'HH:MM' → minutes after midnight; NaN when not a time. */
export const fromHHMM = (hhmm: string): number => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return NaN;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : NaN;
};

/** Window end as "time + days later" — 19:00 for 300 min → 00:00, next day. */
export const splitEnd = (startMinute: number, minutes: number) => {
  const end = startMinute + minutes;
  return { endTime: toHHMM(end), offset: Math.floor(end / 1440) };
};

/** Window length in minutes from the editor fields (NaN / ≤ 0 = invalid). */
export const windowMinutes = (startTime: string, endTime: string, offset: number): number =>
  fromHHMM(endTime) + offset * 1440 - fromHHMM(startTime);

/** "Every Mon–Fri · 19:00 → 00:00 next day" for the list and the editor. */
export const scheduleSentence = (days: number[], startMinute: number, minutes: number): string => {
  if (!days.length) return 'Tick at least one day.';
  if (!(minutes > 0)) return 'The end must be after the start.';
  if (minutes > 7 * 1440) return 'A window can last at most 7 days.';
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const consecutive = sorted.length >= 3 && sorted[sorted.length - 1] - sorted[0] === sorted.length - 1;
  const dayText = consecutive
    ? `${WEEKDAYS[sorted[0] - 1]}–${WEEKDAYS[sorted[sorted.length - 1] - 1]}`
    : sorted.map((d) => WEEKDAYS[d - 1]).join(', ');
  const { endTime, offset } = splitEnd(startMinute, minutes);
  const offsetText = END_OFFSETS.find((o) => o.value === offset)?.label ?? `${offset} days later`;
  return `Every ${dayText} · ${toHHMM(startMinute)} → ${endTime} ${offsetText}`;
};

const BRUSSELS = 'Europe/Brussels';
const DAY_MS = 86400000;

/** Brussels wall-clock time of `now`, as a UTC timestamp (so date maths ignores DST). */
const brusselsWallNow = (now: Date): number => {
  const p: Record<string, number> = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: BRUSSELS, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric',
  }).formatToParts(now).forEach(({ type, value }) => (p[type] = Number(value)));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
};

/** "Sat 24/10 00:10" from a wall-clock UTC timestamp. */
const wallText = (wall: number): string => {
  const d = new Date(wall);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${WEEKDAYS[(d.getUTCDay() + 6) % 7]} ${dd}/${mm} ${toHHMM(d.getUTCHours() * 60 + d.getUTCMinutes())}`;
};

export interface ScheduleWindow {
  live: boolean;
  text: string;
}

/** The current (or next) windows in Brussels time, skipping windows that start
 *  in the pause (inclusive dates 'YYYY-MM-DD'). Same rule as the theme. */
export const upcomingWindows = (
  days: number[], startMinute: number, minutes: number,
  pausedFrom = '', pausedUntil = '', count = 2, now = new Date(),
): ScheduleWindow[] => {
  if (!days.length || !(minutes > 0) || minutes > 7 * 1440 || Number.isNaN(startMinute)) return [];
  const nowWall = brusselsWallNow(now);
  const today = nowWall - (nowWall % DAY_MS);
  const out: ScheduleWindow[] = [];
  for (let k = -8; k <= 21 && out.length < count; k++) {
    const day = today + k * DAY_MS;
    if (!days.includes(((new Date(day).getUTCDay() + 6) % 7) + 1)) continue;
    const iso = new Date(day).toISOString().slice(0, 10);
    if (pausedFrom && pausedUntil && iso >= pausedFrom && iso <= pausedUntil) continue;
    const start = day + startMinute * 60000;
    const end = start + minutes * 60000;
    if (end <= nowWall) continue;
    // Ending at midnight: also name the last full day, e.g. "(last day Sun 25/10)".
    const lastDay = end % DAY_MS === 0 && end - DAY_MS > start
      ? ` (last day ${wallText(end - DAY_MS).slice(0, 9)})` : '';
    // Ending at midnight of its own day (nightly): "Tue 20/10 19:00 → 24:00".
    const endText = end === day + DAY_MS ? '24:00' : wallText(end);
    out.push({ live: start <= nowWall, text: `${wallText(start)} → ${endText}${lastDay}` });
  }
  return out;
};
